/**
 * @module Service
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
	AddUserResponse,
	Auth0UserVerification,
	User,
	UserWithDocument,
	Individual,
	OrderUserPair,
	UserAgentRef,
	UserBulkActionInput,
	UserBulkActionOutput,
} from '@feature/users/shared';
import { environment } from '@env/environment';
import { BaseHttpService } from '@app/services/base-http.service';
import { Page, PageRequest } from '@app/models/page.model';
import { Document } from '@feature/document-center/shared/document.model';

/**
 * Service for managing user-related operations within the application.
 *
 * This service extends the BaseHttpService to provide additional functionality
 * specific to user management, such as fetching user data, updating user profiles,
 * and handling authentication. It uses Angular HttpClient for making HTTP requests
 * to the backend API. It is provided at the root level, making it available
 * throughout the application.
 *
 * @service
 * @extends {BaseHttpService}
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class UserService extends BaseHttpService {
	/**
	 * Options for HTTP requests, including headers.
	 * @type {{ headers: HttpHeaders }}
	 * @note Used for the CRUD operations on the user.
	 */
	options: { headers: HttpHeaders } = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	/**
	 * Creates an instance of the UserService.
	 *
	 * @constructor
	 * @param {HttpClient} _http - The HttpClient used for making HTTP requests.
	 */
	constructor(private _http: HttpClient) {
		super(_http);
	}

	/**
	 * Retrieves recipients based on the provided filter.
	 *
	 * @param {string} filter - The filter string to search for payee-agent pairs or publishers.
	 * @param {number | null} payerCompanyContextId - Optional unique identifier of a payer company context.
	 * @returns {Observable<Array<OrderUserPair>>} An observable containing the array of payee-agent pairs or publishers.
	 */
	public getOrderUserPairs(filter: string, payerCompanyContextId: number | null = null): Observable<Array<OrderUserPair>> {
		let searchParams: HttpParams = new HttpParams();
		if (payerCompanyContextId) searchParams = searchParams.append('payerCompanyContextId', payerCompanyContextId);
		filter = encodeURIComponent(filter);
		return this._http.get<Array<OrderUserPair>>(`${environment.config.apiUrl}/order-user-pairs/${filter}`, { params: searchParams });
	}

	/**
	 * Retrieves a user by their unique identifier.
	 *
	 * @param {any} id - The unique identifier of the user.
	 * @returns {Observable<User>} An observable containing the user object.
	 */
	public getById(id: any): Observable<User> {
		id = encodeURIComponent(id);
		return this._http.get<User>(`${environment.config.apiUrl}/users/${id}`, this.options);
	}

	/**
	 * Retrieves a user by their email address.
	 *
	 * @param {string} email - The email address of the user.
	 * @returns {Observable<User>} An observable containing the user object.
	 */
	public getByEmail(email: string): Observable<User> {
		email = encodeURIComponent(email);
		return this._http.get<User>(`${environment.config.apiUrl}/users/email/${email}`);
	}

	/**
	 * Get payer documents per required document category.
	 *
	 * @param {number} userId
	 * @param {number} categoryId
	 * @returns {Observable<Document[]>}
	 */
	public getUserDocumentsPerRequiredDocumentCategory(userId: number, categoryId: number): Observable<Array<Document>> {
		let performerParams = new HttpParams();
		performerParams = performerParams.append('categoryId', (categoryId != null ? categoryId.toString() : ''));
		return this._http.get<Array<Document>>(`${environment.config.apiUrl}/users/${userId}/required-documents`, { params: performerParams });
	}

	/**
	 * Retrieves clients associated with a specific agency.
	 *
	 * @param {number} agencyId - The unique identifier of the agency.
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter clients.
	 * @returns {Observable<Page<UserAgentRef>>} An observable containing the paginated list of user agents.
	 */
	public getClients(agencyId: number, pageable?: PageRequest, search?: string): Observable<Page<UserAgentRef>> {
		return this._http.get<Page<UserAgentRef>>(`${environment.config.apiUrl}/companies/${agencyId}/clients`, { params: this.params(pageable, search) });
	}

	/**
	 * Retrieves a specific client associated with an agency.
	 *
	 * @param {number} agencyId - The unique identifier of the agency.
	 * @param {number} clientId - The unique identifier of the client.
	 * @returns {Observable<UserAgentRef>} An observable containing the user agent reference for the client.
	 */
	public getClient(agencyId: number, clientId: number): Observable<UserAgentRef> {
		return this._http.get<UserAgentRef>(`${environment.config.apiUrl}/companies/${agencyId}/clients/${clientId}`);
	}

	/**
	 * Retrieves users based on their type, with optional pagination and search parameters.
	 *
	 * @param {string} type - The type of users to retrieve.
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter users.
	 * @returns {Observable<Page<UserWithDocument>>} An observable containing the paginated list of users with documents.
	 */
	public getUsers(type: string, pageable?: PageRequest, search?: string): Observable<Page<UserWithDocument>> {
		return this._http.get<Page<UserWithDocument>>(`${environment.config.apiUrl}/users/type/${type}`, { params: this.params(pageable, search) });
	}

	/**
	 * Retrieves agents associated with a specific client.
	 *
	 * @param {number} clientId - The unique identifier of the client.
	 * @param {Array<number>} [clientIds] - Optional array of client IDs to filter agents.
	 * @returns {Observable<UserAgentRef[]>} An observable containing the list of user agent references.
	 */
	public getAgents(clientId: number, clientIds?: Array<number>): Observable<UserAgentRef[]> {
		// ETT-136 Allow to search for agent-client pairs.
		let parameters = new HttpParams();
		if (clientIds != null && clientIds?.length > 0) {
			clientIds.map((id: number) => parameters = parameters.append('clients', id));
		}
		return this._http.get<UserAgentRef[]>(`${environment.config.apiUrl}/users/${clientId}/agents`, { params: parameters });
	}

	/**
	 * Checks if a client can be added based on the agency ID and talent email.
	 *
	 * @param {number} agencyId - The unique identifier of the agency.
	 * @param {string} talentEmail - The email address of the talent to check.
	 * @returns {Observable<AddUserResponse>} An observable containing the response indicating if the client can be added.
	 */
	public canAddClient(agencyId: number, talentEmail: string): Observable<AddUserResponse> {
		talentEmail = encodeURIComponent(talentEmail);
		return this._http.get<AddUserResponse>(`${environment.config.apiUrl}/companies/${agencyId}/can-add-talent/${talentEmail}`);
	}

	/**
	 * Adds a client to a specific agency.
	 *
	 * @param {number} agencyId - The unique identifier of the agency.
	 * @param {number} clientId - The unique identifier of the client to add.
	 * @returns {Observable<UserAgentRef>} An observable containing the user agent reference for the added client.
	 */
	public addClient(agencyId: number, clientId: number): Observable<UserAgentRef> {
		return this._http.put<UserAgentRef>(`${environment.config.apiUrl}/companies/${agencyId}/clients/${clientId}`, null);
	}

	/**
	 * Accepts an agent request based on the provided key.
	 *
	 * @param {string} key - The unique key identifying the agent request.
	 * @returns {Observable<UserAgentRef>} An observable containing the user agent reference for the accepted agent.
	 */
	public acceptAgent(key: string): Observable<UserAgentRef> {
		return this._http.put<UserAgentRef>(`${environment.config.apiUrl}/agent-request/${key}/accept`, null);
	}

	/**
	 * Declines an agent request based on the provided key.
	 *
	 * @param {string} key - The unique key identifying the agent request.
	 * @returns {Observable<UserAgentRef>} An observable containing the user agent reference for the declined agent.
	 */
	public declineAgent(key: string): Observable<UserAgentRef> {
		return this._http.put<UserAgentRef>(`${environment.config.apiUrl}/agent-request/${key}/decline`, null);
	}

	/**
	 * Updates the reference of an agent based on the provided user agent reference.
	 *
	 * @param {UserAgentRef} ref - The user agent reference to update.
	 * @param {string} [step] - Optional step parameter for the update process.
	 * @returns {Observable<UserAgentRef>} An observable containing the updated user agent reference.
	 */
	public updateAgentRef(ref: UserAgentRef, step?: string): Observable<UserAgentRef> {
		let parameters = new HttpParams();
		parameters = parameters.append('step', step);
		return this._http.put<UserAgentRef>(`${environment.config.apiUrl}/agent-request/${ref.id}`, ref, { params: parameters });
	}

	/**
	 * Creates a new user account based on the specified role.
	 *
	 * This method determines the type of user to create (talent, customer, or admin) based on the
	 * provided role. It delegates the creation process to the appropriate method for each user type.
	 *
	 * @param {User} user - The user object containing the details of the user to be created.
	 * @param {string} role - The role of the user being created (e.g., 'talent', 'customer', 'admin').
	 * @returns {Observable<User>} An observable containing the created user object or null if the role is invalid.
	 */
	public createUser(user: User, role: string): Observable<User> {
		if (role.toLocaleLowerCase() === 'talent') {
			return this.createTalent(user);
		} else if (role.toLocaleLowerCase() === 'customer') {
			return this.createCustomer(user);
		} else if (role.toLocaleLowerCase() === 'admin') {
			return this.createAdministrator(user);
		}
		return of(null);
	}

	/**
	 * Creates a new user account associate with the talent role. Optionally associates it with a company context.
	 *
	 * This method sends a POST request to create a talent user, including any necessary parameters
	 * such as the company context ID.
	 *
	 * @param {User} talent - The user object representing the talent to be created.
	 * @param {number} [companyContextId] - Optional ID of the company context to associate with the talent.
	 * @returns {Observable<User>} An observable containing the created talent user object.
	 */
	public createTalent(talent: User, companyContextId?: number): Observable<User> {
		let performerParams = new HttpParams();
		performerParams = performerParams.append('companyContextId', (companyContextId != null ? companyContextId.toString() : ''));
		return this._http.post<User>(`${environment.config.apiUrl}/payees`, talent, { params: performerParams });
	}

	/**
	 * Creates a new user account associate with the customer role.
	 *
	 * This method sends a POST request to create a customer user.
	 *
	 * @param {User} customer - The user object representing the customer to be created.
	 * @returns {Observable<User>} An observable containing the created customer user object.
	 */
	public createCustomer(customer: User): Observable<User> {
		return this._http.post<User>(`${environment.config.apiUrl}/payers`, customer, this.options);
	}

	/**
	 * Creates a new user account associate with the talent/client role. Optionally associates it with the agency.
	 *
	 * This method sends a POST request to create a performer user, including parameters for the
	 * company context ID and whether to send an email notification.
	 *
	 * @param {User} performer - The user object representing the performer to be created.
	 * @param {number} [companyContextId] - Optional ID of the agency context to associate with the performer.
	 * @param {boolean} [needToSendEmail=true] - Optional flag indicating whether to send an email notification.
	 * @returns {Observable<User>} An observable containing the created performer user object.
	 */
	public createPerformer(performer: User, companyContextId?: number, needToSendEmail?: boolean): Observable<User> {
		let performerParams = new HttpParams();
		performerParams = performerParams.append('companyContextId', (companyContextId != null ? companyContextId.toString() : ''));
		performerParams = performerParams.append('needToSendEmail', (needToSendEmail != null ? String(needToSendEmail) : 'true'));
		return this._http.post<User>(`${environment.config.apiUrl}/performers`, performer, { params: performerParams });
	}

	/**
	 * Creates a new user account associated with administrator role.
	 *
	 * This method sends a POST request to create an administrator user.
	 *
	 * @param {User} customer - The user object representing the administrator to be created.
	 * @returns {Observable<User>} An observable containing the created administrator user object.
	 */
	public createAdministrator(customer: User): Observable<User> {
		return this._http.post<User>(`${environment.config.apiUrl}/administrators`, customer, this.options);
	}

	/**
	 * Deletes a user.
	 *
	 * @param {number} userId - The user's unique identifier to delete
	 * @returns {Observable<any>}
	 */
	public delete(userId: number): Observable<any> {
		return this._http.delete(`${environment.config.apiUrl}/users/${userId}`);
	}

	/**
	 * Updates the information associated with a user.
	 *
	 * @param {User} user - The user object containing updated information.
	 * @param {boolean} [isAccountPage=false] - An optional flag indicating the update is part of the account page.
	 * @returns {Observable<User>}
	 */
	public updateUser(user: User, isAccountPage: boolean = false): Observable<User> {
		let performerParams = new HttpParams();
		if (isAccountPage) performerParams = performerParams.append('isAccountPage', 'true');
		return this._http.put<User>(`${environment.config.apiUrl}/users/${user.id}`, user, { params: performerParams });
	}

	/**
	 * Resends the verification email to a user.
	 *
	 * @param {number} userId - The user's unique identifier to send the verification email to.
	 * @returns {Observable<void>}
	 */
	public resendVerifyEmail(userId: number): Observable<void> {
		return this._http.put<void>(`${environment.config.apiUrl}/users/${userId}/verify-email`, null);
	}

	/**
	 * Checks if the user's email is verified.
	 *
	 * @param {number} userId - The user's unique identifier to send the verification email to.
	 * @returns {Observable<Auth0UserVerification>}
	 */
	public isEmailVerified(userId: number): Observable<Auth0UserVerification> {
		return this._http.get<Auth0UserVerification>(`${environment.config.apiUrl}/users/${userId}/verify-email`);
	}

	/**
	 * Retrieves an individual based on the provided user id.
	 *
	 * @param {number} userId - The user's unique identifier whose individual information is to be retrieved.
	 * @returns {Observable<Individual>}
	 */
	public getIndividualByUserId(userId: number): Observable<Individual> {
		return this._http.get<Individual>(`${environment.config.apiUrl}/individuals/users/${userId}`);
	}

	/**
	 * Creates an individual profile reference.
	 *
	 * @param {Individual} individual - An individual containing information to be inserted.
	 * @returns {Observable<Individual>}
	 */
	public createIndividual(individual: Individual): Observable<Individual> {
		return this._http.post<Individual>(`${environment.config.apiUrl}/individuals/users/${individual.userId}`, individual, this.options);
	}

	/**
	 * Updates an individual profile reference.
	 *
	 * @param {Individual} individual - An individual containing the updated information.
	 * @param {string} [step] - An optional step identifier for tracking the update process.
	 * @returns {Observable<Individual>}
	 */
	public updateIndividual(individual: Individual, step?: string): Observable<Individual> {
		let profileParams = new HttpParams();
		profileParams = profileParams.append('step', (step != null ? step : null));
		return this._http.put<Individual>(`${environment.config.apiUrl}/individuals/${individual.id}`, individual, { params: profileParams });
	}

	/**
	 * Checks if a client exists for a given agent.
	 *
	 * This method sends a POST request to verify the existence of a client associated with the specified
	 * agent. It constructs the necessary parameters, including agent ID, agency ID, and company context ID,
	 * and returns an observable indicating whether the client exists.
	 *
	 * @param {number} agentId - The unique identifier of the agent.
	 * @param {User} client - The user object representing the client to check.
	 * @param {number} [companyContextId] - Optional ID of the company context.
	 * @param {number} [agencyId] - Optional ID of the agency.
	 * @returns {Observable<boolean>} An observable that emits true if the client exists, otherwise false.
	 */
	public doesClientExist(agentId: number, client: User, companyContextId?: number, agencyId?: number): Observable<boolean> {
		let performerParams = new HttpParams();
		performerParams = performerParams.append('agentId', (agentId != null ? agentId.toString() : ''));
		performerParams = performerParams.append('agencyId', (agencyId != null ? agencyId.toString() : ''));
		performerParams = performerParams.append('companyContextId', (companyContextId != null ? companyContextId.toString() : ''));
		return this._http.post<boolean>(`${environment.config.apiUrl}/agent/does-client-exist`, client, { params: performerParams });
	}

	/**
	 * Checks if a talent can be added to the platform based on their email address.
	 *
	 * This method sends a GET request to determine if a performer can be added using the provided email.
	 * It returns an observable containing the response indicating whether the addition is allowed.
	 *
	 * @param {string} email - The email address of the performer to check.
	 * @returns {Observable<AddUserResponse>} An observable containing the response regarding the addition of the performer.
	 */
	public canAddPerformer(email: string): Observable<AddUserResponse> {
		return this._http.get<AddUserResponse>(`${environment.config.apiUrl}/can-add-performer/${email}`, this.options);
	}

	/**
	 * Performs a bulk action on users based on the provided input.
	 *
	 * This method sends a PUT request to execute a bulk action on users, using the specified input
	 * parameters. It returns an observable containing the result of the bulk action.
	 *
	 * @param {UserBulkActionInput} input - The input object containing details of the bulk action to perform.
	 * @returns {Observable<UserBulkActionOutput>} An observable containing the output of the bulk action.
	 */
	public bulkAction(input: UserBulkActionInput): Observable<UserBulkActionOutput> {
		return this._http.put<UserBulkActionOutput>(`${environment.config.apiUrl}/users/bulk-action`, input, this.options);
	}

	/**
	 * @ignore
	 * Sends the password reset request to a user based on their email address.
	 *
	 * This method constructs an HTTP request to initiate the password reset process for the user
	 * with the specified email. It sends a request to the Auth0 API to change the user's password.
	 *
	 * @param {string} userEmail - The email address of the user whose password is to be reset.
	 * @returns {Observable<any>} An observable containing the response from the password reset request.
	 */
	public resetPassword(userEmail: string) {
		// if (userEmail === undefined) return;
		//
		// let params = new HttpParams();
		// params = params.append('client_id', environment.auth0.clientId)
		// .append('email', userEmail)
		// .append('connection', environment.auth0.dbConnectionName);
		// return this._http.post(`${environment.auth0.authDomain}/dbconnections/change_password`, params)
		// .pipe((response: any) => {
		// console.log(response);
		// });
	}
}
